import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { Shield, Lock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { API_URL } from '../config';
import '../styles/Auth.css';


const MagicAccess = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  
  const [status, setStatus] = useState('verifying'); // verifying, needs_password, logging_in, error, success
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/verify-token/${token}`);
      setEmail(response.data.email);
      
      if (response.data.needs_password) {
        setStatus('needs_password');
      } else {
        // User already has password, auto-login
        await handleMagicLogin();
      }
    } catch (err) {
      console.error('Token verification failed:', err);
      setError(err.response?.data?.detail || 'This link is invalid or has expired.');
      setStatus('error');
    }
  };

  const handleMagicLogin = async () => {
    setStatus('logging_in');
    setLoading(true);
    
    try {
      await axios.post(
        `${API_URL}/api/auth/magic-login`,
        { token },
        { withCredentials: true }
      );
      
      await checkAuth();
      setStatus('success');
      
      // Redirect to dashboard after brief success message
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err) {
      console.error('Magic login failed:', err);
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(
        `${API_URL}/api/auth/set-password`,
        { token, password },
        { withCredentials: true }
      );
      
      await checkAuth();
      setStatus('success');
      
      // Redirect to dashboard
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err) {
      console.error('Set password failed:', err);
      setError(err.response?.data?.detail || 'Failed to set password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'verifying':
      case 'logging_in':
        return (
          <div className="auth-status-container">
            <Loader2 className="auth-spinner" size={48} />
            <h2>{status === 'verifying' ? 'Verifying your access...' : 'Logging you in...'}</h2>
            <p>Please wait a moment.</p>
          </div>
        );
      
      case 'needs_password':
        return (
          <div className="auth-form-container">
            <div className="auth-header">
              <Lock size={32} className="auth-icon" />
              <h2>Set Your Password</h2>
              <p>Welcome! Create a password to secure your account.</p>
              {email && <p className="auth-email-display">{email}</p>}
            </div>
            
            <form onSubmit={handleSetPassword} className="auth-form">
              {error && (
                <div className="auth-error">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  required
                  minLength={8}
                  data-testid="password-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              
              <button 
                type="submit" 
                className="auth-submit-btn"
                disabled={loading}
                data-testid="set-password-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="btn-spinner" size={18} />
                    Setting Password...
                  </>
                ) : (
                  'Set Password & Access Course'
                )}
              </button>
            </form>
          </div>
        );
      
      case 'success':
        return (
          <div className="auth-status-container success">
            <CheckCircle size={48} className="success-icon" />
            <h2>You're in!</h2>
            <p>Redirecting to your dashboard...</p>
          </div>
        );
      
      case 'error':
        return (
          <div className="auth-status-container error">
            <AlertCircle size={48} className="error-icon" />
            <h2>Access Link Issue</h2>
            <p>{error}</p>
            <div className="auth-error-actions">
              <button 
                onClick={() => navigate('/login')}
                className="auth-secondary-btn"
              >
                Go to Login
              </button>
              <button 
                onClick={() => navigate('/request-access')}
                className="auth-link-btn"
              >
                Request New Access Link
              </button>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <>
      <Header />
      <div className="auth-page magic-access-page">
        <div className="auth-container">
          <div className="auth-logo-container">
            <Shield size={40} />
            <span>All Secured</span>
          </div>
          
          {renderContent()}
        </div>
      </div>
    </>
  );
};

export default MagicAccess;
